import { z } from 'zod';

// A missing, null or malformed field reads as absent, so the stored record falls back to its default.
const optional = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);
const id = z.union([z.string(), z.number().transform(String)]);

// The PII QCS returns with a registration. Older registrations come back without a phone number.
const CreditProfilePiiSchema = z.object({
  firstName: optional(z.string()),
  middleName: optional(z.string()),
  lastName: optional(z.string()),
  dateOfBirth: optional(z.string()),
  addressLine1: optional(z.string()),
  city: optional(z.string()),
  state: optional(z.string()),
  zipCode: optional(z.string()),
  phoneNumber: optional(z.string()),
  creditProfileRegistrationId: optional(id),
  creditProfileCreatedAt: optional(z.string()),
  creditProfileModifiedAt: optional(z.string()),
});

// The registration as qcs-creditscore-svc sends it; datasetId is never sent.
const CreditProfileRegistrationSchema = z.object({
  id: optional(id),
  providerCreditProfileRegistrationId: optional(z.string()),
  creditProviderId: optional(z.string()),
  status: optional(z.string()),
  creditProfileId: optional(z.number()),
  creditReportId: optional(z.number()),
  creditProfileRegistrationModifiedAt: optional(z.string()),
  creditProfileRegistrationCreatedAt: optional(z.string()),
  creditProfile: optional(CreditProfilePiiSchema),
  numberOfQuestions: optional(z.number()),
});

export type CreditProfilePii = z.infer<typeof CreditProfilePiiSchema>;
type CreditProfileRegistration = z.infer<typeof CreditProfileRegistrationSchema>;

/** Validates a credit profile registration response body; undefined when it is not an object. */
export const parseCreditProfileRegistration = (data: unknown): CreditProfileRegistration | undefined => {
  const result = CreditProfileRegistrationSchema.safeParse(data);
  return result.success ? result.data : undefined;
};

/**
 * Validates the registrations list, dropping entries that are not objects. A body that is not a list throws,
 * so the request fails instead of replacing the stored registrations with none.
 */
export const parseCreditProfileRegistrations = (data: unknown): CreditProfileRegistration[] => {
  if (data === null || data === undefined) return [];
  if (!Array.isArray(data)) throw new Error('Credit profile registrations response is not a list');
  return data.flatMap((entry: unknown) => parseCreditProfileRegistration(entry) ?? []);
};
